import { Close } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import CropIcon from "@mui/icons-material/Crop";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Modal, Tooltip } from "@mui/material";
import { styled as MuiStyled } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from "react";
import DraggableList from "react-draggable-list";
import { styled } from "styled-components";
import AudioRecorder from "../../../../assets/icons/AudioRecorder";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import Button from "../../../Button";
import ImageCropper from "../../../ImageCropper";
import Input from "../../../Input";
import TextView from "../../../TextView";
import MediaSelection from "../../Shared/MediaSelection";

const Container = styled.div`
  min-height: 40px;
  height: 6vh;
  align-items: center;
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 5px 20px 40px 1fr auto;
  grid-gap: 8px;
`;

const DragIcon = MuiStyled(DragIndicatorIcon)`
  font-weight: 600;
  font-size: 25px;
  cursor: grab;
  color: #bbbbbb;
`;

const AudioIcon = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
`;

const IconsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 3px;
  cursor: pointer;
  width: 100%;
  gap: 5px;
`;

const Icon = styled.img`
  height: 16px;
  width: 16px;
`;

const ModalContainer = styled.div`
  position: absolute;
  width: 400px;
  background-color: white;
  box-shadow: 0px 0px 9.3px 0px #3b3b3b1c;
  padding: 16px;
  border-radius: 8px;
  top: 30%;
  right: 10%;
  overflow: hidden;
`;

const AddIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid #e1e1e1;
  background-color: ${colors.background};
  margin: auto;
  cursor: pointer;
  margin-top: 10px;
`;

const ModalHeader = styled.div`
  display: flex;
  background-color: ${colors.background};
  justify-content: space-between;
  padding: 16px;
`;

const ModalContent = styled.div`
  padding: 16px;
`;

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: 100px 80px;
  grid-gap: 10px;
  justify-content: flex-end;
`;

const ImageWrapper = styled.div`
  cursor: pointer;
  position: relative;
`;

const CropperWrapper = styled.div`
  background-color: #8c8c8c90;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
`;

export default function AudioList({
  audios = [],
  setAudios,
  onClickAdd,
  iconColor,
}) {
  const [openAudioSelection, setOpenAudioSelection] = useState(false);
  const [openImageSelection, setOpenImageSelection] = useState(false);
  const [openImageCropper, setOpenImageCropper] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);

  const mediaRef = useRef(null);
  const containerRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mediaRef.current && !mediaRef.current.contains(event.target)) {
        setOpenAudioSelection(false);
        setCurrentItem(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mediaRef]);

  const _onListChange = (newList) => {
    setAudios(newList);
  };

  const handleOpenAudioSelection = (item) => {
    setCurrentItem(item);
    setOpenAudioSelection(true);
  };

  const handleOpenImageSelection = (item) => {
    setCurrentItem(item);
    setOpenImageSelection(true);
  };

  const handleTitleChange = (id, value) => {
    const temp = [...audios];
    const index = temp.findIndex((val) => val.id == id);
    temp[index] = { ...temp[index], title: value };
    setAudios(temp);
  };

  const onDeleteItem = (id) => {
    setAudios(audios.filter((audio) => audio.id !== id));
  };

  const onReplaceItem = (_, value) => {
    const index = audios.findIndex((item) => item.id === currentItem.id);
    if (index !== -1) {
      const updatedArray = [...audios];
      updatedArray[index] = value;
      setAudios(updatedArray);
      setOpenAudioSelection(false);
    }
  };

  const handleOpenImageCropper = (item) => {
    setHoveredItem(null);
    setCurrentItem(item);
    setOpenImageCropper(true);
  };

  const onImageChange = (_, value) => {
    const index = audios.findIndex((item) => item.id === currentItem.id);
    if (index !== -1) {
      const updatedArray = [...audios];
      updatedArray[index] = { ...updatedArray[index], image: value.src };
      setAudios(updatedArray);
      setOpenImageSelection(false);
    }
  };

  const ListTemplate = ({ item, index, dragHandleProps }) => {
    const { onMouseDown, onTouchStart } = dragHandleProps;
    const [title, setTitle] = useState(item.title);
    const [openTitlePopup, setOpenTitlePopup] = useState(false);

    return (
      <div>
        <Container>
          <span
            style={{
              fontFamily: "DM Sans",
              fontSize: "12px",
              fontWeight: 400,
              color: "#565556",
            }}
          >
            {index + 1}
          </span>
          <DragIcon
            className="disable-select dragHandle"
            onTouchStart={(e) => {
              e.preventDefault();
              e.target.style.backgroundColor = "blue";
              document.body.style.overflow = "hidden";
              onTouchStart(e);
            }}
            onMouseDown={(e) => {
              document.body.style.overflow = "hidden";
              onMouseDown(e);
            }}
            onTouchEnd={(e) => {
              e.target.style.backgroundColor = "black";
              document.body.style.overflow = "visible";
            }}
            onMouseUp={() => {
              document.body.style.overflow = "visible";
            }}
          />

          {item.image ? (
            <ImageWrapper
              onMouseEnter={() => setHoveredItem(item.id)} // Set hover state when mouse enters
              onMouseLeave={() => setHoveredItem(null)} // Reset hover state when mouse leaves
            >
              <AudioIcon src={item.image} />
              {hoveredItem === item.id && !item.embed && (
                <CropperWrapper>
                  <CropIcon
                    onClick={() => handleOpenImageCropper(item)}
                    fontSize="12px"
                    style={{ color: "white" }}
                  />
                </CropperWrapper>
              )}
            </ImageWrapper>
          ) : (
            <AudioRecorder width={40} height={40} fill={iconColor} />
          )}

          <TextView style={{ WebkitLineClamp: 2 }}>{item.title}</TextView>

          <IconsContainer>
            {!item.embed && (
              <>
                <Tooltip title="Edit Title">
                  <Icon
                    src={ASSETS.segmentEditor.EditPencil}
                    onClick={() => setOpenTitlePopup(true)}
                  />
                </Tooltip>
                <Tooltip title="Edit Thumbnail">
                  <Icon
                    src={ASSETS.segmentEditor.ReplaceImage}
                    onClick={() => handleOpenImageSelection(item)}
                  />
                </Tooltip>
              </>
            )}
            <Tooltip title="Replace Audio">
              <Icon
                src={ASSETS.ReplaceAudio}
                onClick={() => handleOpenAudioSelection(item)}
              />
            </Tooltip>
            <Tooltip title="Delete Audio">
              <Icon
                src={ASSETS.segmentEditor.DeleteOutline}
                onClick={() => onDeleteItem(item.id)}
              />
            </Tooltip>
          </IconsContainer>
        </Container>
        {openTitlePopup && (
          <Modal open={openTitlePopup} onClose={() => setOpenTitlePopup(false)}>
            <ModalContainer style={{ padding: 0 }}>
              <ModalHeader>
                <TextView variant="subtitle">Edit Audio Name</TextView>
                <Close
                  style={{ cursor: "pointer" }}
                  onClick={() => setOpenTitlePopup(false)}
                />
              </ModalHeader>
              <ModalContent>
                <Input
                  variant={"secondary"}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <ButtonsContainer>
                  <Button
                    variant={"secondary"}
                    onClick={() => setOpenTitlePopup(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={() => handleTitleChange(item.id, title)}>
                    Save
                  </Button>
                </ButtonsContainer>
              </ModalContent>
            </ModalContainer>
          </Modal>
        )}
      </div>
    );
  };

  return (
    <div>
      <DraggableList
        itemKey="id"
        template={(props) => (
          <ListTemplate
            {...props}
            index={audios.findIndex((i) => i.id === props.item.id)}
          />
        )}
        list={audios}
        onMoveEnd={(newList) => _onListChange(newList)}
        container={() => containerRef.current}
      />

      <AddIconContainer onClick={onClickAdd}>
        <AddIcon style={{ color: colors.purple }} />
      </AddIconContainer>

      {currentItem && (
        <Modal
          open={openAudioSelection}
          onClose={() => setOpenAudioSelection(false)}
        >
          <ModalContainer>
            <MediaSelection
              type="audio"
              handleChange={onReplaceItem}
              categories={["Upload", "Embed Code"]}
              media={currentItem}
            />
          </ModalContainer>
        </Modal>
      )}

      {currentItem && (
        <Modal
          open={openImageSelection}
          onClose={() => setOpenImageSelection(false)}
        >
          <ModalContainer>
            <MediaSelection type="image" handleChange={onImageChange} />
          </ModalContainer>
        </Modal>
      )}

      <ImageCropper
        open={openImageCropper}
        handleClose={() => setOpenImageCropper(false)}
        src={currentItem?.image}
        handleEditImage={(image) => onImageChange(null, { src: image })}
        lockedAspectRatio={1}
      />
    </div>
  );
}
